import React from "react";
import { Users, Link2, BadgeCheck, X, CheckCircle2, AlertTriangle, Settings2 } from "lucide-react";

// Header banner: three linkage pills. They alert (colored) while anything is
// pending, and open the breakdown panel on click. Display-only for now.
const PILLS = [
  { key: "payeesAligned", tone: "amber", icon: Users, noun: "payees", verb: "aligned", pendWord: "pending", section: "payees" },
  { key: "payersLinked", tone: "blue", icon: Link2, noun: "payers", verb: "linked", pendWord: "pending", section: "payers" },
  { key: "payeeNpis", tone: "violet", icon: BadgeCheck, noun: "payee NPIs", verb: "ready", pendWord: "need NPI", section: "npis" },
];

export function LinkageBanner({ stats, loading, onOpen }) {
  if (loading || !stats) {
    return (
      <div className="pp-linkbanner" data-testid="pp-linkage-banner">
        {PILLS.map((p) => <div key={p.key} className="pp-linkpill pp-skel"><span className="pp-shimmer" style={{ width: 150, height: 12, borderRadius: 6 }} /></div>)}
      </div>
    );
  }
  return (
    <div className="pp-linkbanner" data-testid="pp-linkage-banner">
      {PILLS.map((p) => {
        const s = stats[p.key] || { ready: 0, total: 0 };
        const pending = Math.max(0, s.total - s.ready);
        const done = pending === 0 && s.total > 0;
        const Icon = p.icon;
        return (
          <button key={p.key} className={`pp-linkpill pp-${p.tone} ${done ? "pp-done" : "pp-alert"}`}
            onClick={() => onOpen?.(p.section)} data-testid={`pp-linkpill-${p.section}`}>
            <span className="pp-lp-ic"><Icon size={13} /></span>
            <span className="pp-lp-txt">{s.ready} of {s.total} {p.noun} {p.verb}</span>
            {pending > 0 ? (
              <span className="pp-lp-badge" data-testid={`pp-linkpill-pending-${p.section}`}>{pending} {p.pendWord}</span>
            ) : (
              <span className="pp-lp-badge pp-ok"><CheckCircle2 size={11} /> all set</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

const SECTIONS = {
  payers: { title: "Payers · EDI link", noun: "payer", readyKey: "linked", readyLabel: "Linked", pendLabel: "Link EDI Payer ID" },
  payees: { title: "Payees · EHI alignment", noun: "payee", readyKey: "ehi", readyLabel: "Aligned", pendLabel: "Pend EHI Link" },
  npis: { title: "Payee NPIs", noun: "payee", readyKey: "npi", readyLabel: "NPI ready", pendLabel: "Add Payee NPI" },
};

export function LinkagePanel({ open, section = "payees", onClose, onSection, data }) {
  if (!open) return null;
  const cfg = SECTIONS[section] || SECTIONS.payees;
  const rows = (section === "payers" ? data?.payers : data?.payees) || [];
  const ready = rows.filter((r) => r[cfg.readyKey]);
  const pending = rows.filter((r) => !r[cfg.readyKey]);
  const ordered = [...pending, ...ready];

  return (
    <>
      <div className="pp-sp-backdrop" onClick={onClose} data-testid="pp-linkage-backdrop" />
      <aside className="pp-sp pp-lk" role="dialog" aria-label="Linkage" data-testid="pp-linkage-panel">
        <div className="pp-sp-head">
          <div>
            <div className="pp-t">{cfg.title} <span className="pp-sn">{ready.length}/{rows.length}</span></div>
            <div className="pp-sp-hint">Review linkage across all payments. The configurator lands here soon.</div>
          </div>
          <button className="pp-sp-close" onClick={onClose} data-testid="pp-linkage-close" aria-label="Close"><X size={16} /></button>
        </div>

        <div className="pp-lk-tabs" data-testid="pp-linkage-tabs">
          {Object.entries(SECTIONS).map(([id, c]) => (
            <button key={id} className={`pp-lk-tab ${id === section ? "pp-on" : ""}`}
              onClick={() => onSection?.(id)} data-testid={`pp-linkage-tab-${id}`}>
              {c.title.split(" · ")[0]}
            </button>
          ))}
        </div>

        <div className="pp-sp-body">
          {rows.length === 0 ? (
            <div className="pp-sp-empty">No {cfg.noun}s found yet. Upload EOBs and they'll appear here.</div>
          ) : (
            ordered.map((r) => {
              const ok = r[cfg.readyKey];
              return (
                <div className="pp-lk-item" key={r.name} data-testid="pp-linkage-item">
                  <div className={`pp-lk-ic ${ok ? "pp-ok" : "pp-pend"}`}>
                    {ok ? <CheckCircle2 size={15} /> : <AlertTriangle size={15} />}
                  </div>
                  <div className="pp-lk-nm" title={r.name}>{r.name}</div>
                  <span className={`pp-lk-chip ${ok ? "pp-ok" : "pp-pend"}`}>{ok ? cfg.readyLabel : cfg.pendLabel}</span>
                </div>
              );
            })
          )}
        </div>

        <div className="pp-lk-foot">
          <Settings2 size={13} />
          <span>Linking &amp; configurator actions arrive in the next pass — this view is read-only for now.</span>
        </div>
      </aside>
    </>
  );
}
